import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { HttpApiResponseInterface } from 'pf-frontend-common/src/module/http/api-response.interface';
import { JsonApiModelInterface } from 'pf-frontend-common/src/module/json-api/model.interface';
import { BrowserStorageLocalServiceInterface } from 'pf-frontend-common/src/service/browser-storage-local/service.interface';

import { PropertyShareModel } from 'common/data/property-share/model';
import { PropertyShareStore } from 'common/data/property-share/store';
import { configGetSite } from 'common/helper/config/get-site';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { DataStore } from 'common/module/data/store';
import { FormPropertyShareInputInterface } from 'common/module/form/property-share/input.interface';
import { FormPropertyShareInputAdapter } from 'common/module/form/property-share/input-adapter';
import { FormPropertyShareModelAdapter } from 'common/module/form/property-share/model-adapter';
import { FormPropertyShareStatechart } from 'common/module/form/property-share/statechart';
import { FormPropertyShareStatechartEvent } from 'common/module/form/property-share/statechart.event';
import { FormPropertyShareStatechartActionEnum } from 'common/module/form/property-share/statechart/action.enum';
import { FormPropertyShareTemplatePropsInterface } from 'common/module/form/property-share/template-props.interface';
import { FormPropertyShareViewStoreEvent } from 'common/module/form/property-share/view-store.event';
import { FormPropertyShareViewStoreOptionsInterface } from 'common/module/form/property-share/view-store-options.interface';
import { GoogleRecaptchaViewStore } from 'common/module/google/recaptcha/view-store';
import { GoogleRecaptchaViewStoreEvent } from 'common/module/google/recaptcha/view-store.event';
import { InputStateInterface } from 'common/module/input/state.interface';
import { InputValidatorInterface } from 'common/module/input/validator.interface';
import { InputValidatorEmail } from 'common/module/input/validator/email';
import { InputValidatorRequired } from 'common/module/input/validator/required';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';

export class FormPropertyShareViewStore extends DataStore {
  /**
   * @inheritDoc
   */
  protected state: FormPropertyShareTemplatePropsInterface;

  /**
   * Model cache key (for form prepopulation)
   */
  protected readonly cachedModelKey: string = 'property-share-form';

  /**
   * Constructor
   */
  constructor(
    protected eventEmitter: EventEmitterInterface,
    protected propertyShareStore: PropertyShareStore,
    protected captcha: GoogleRecaptchaViewStore,
    protected browserStorageLocalService: BrowserStorageLocalServiceInterface,
    private propertyShareStatechart: StatechartStoreInterface
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public initState(): void {
    const descPlaceholder = i18nTranslate(
      'Hi, I found this home on {sitename}. Please check it out and tell me what you think...'
    ).replace('{sitename}', configGetSite().name);

    const cachedModel =
      <PropertyShareModel>this.browserStorageLocalService.getData(this.cachedModelKey) || new PropertyShareModel();

    this.state = {
      model: null,
      captcha: this.captcha.getState(),
      captchaRequired: true,
      status: {
        submitted: false,
        submitting: false,
      },
      input: {
        email: {
          value: cachedModel.email,
          placeholder: i18nTranslate('Your email'),
        },
        friendEmail: {
          value: cachedModel.friend_email,
          placeholder: i18nTranslate(`Friend's email`),
        },
        message: {
          value: descPlaceholder,
          placeholder: descPlaceholder,
        },
        captcha: {
          value: null,
        },
      },
      onChange: this.onChange,
      onBlur: this.onBlur,
      onSubmit: this.onSubmit,
      onClickClose: this.onClickClose,
    };
  }

  /**
   * Initialize view store state
   */
  public initialize(options?: FormPropertyShareViewStoreOptionsInterface): void {
    this.propertyShareStatechart.initialize({
      statechart: FormPropertyShareStatechart,
    });

    this.destroy();

    // Reset state
    this.initState();

    if (options && options.model) {
      this.updateModel(options.model);
    }

    this.observers();
  }

  /**
   * Remove event listeners
   */
  public destroy(): void {
    this.captcha.getEventEmitter().removeListener(GoogleRecaptchaViewStoreEvent.change, this.onChangeCaptchaViewStore);

    this.propertyShareStatechart
      .getEventEmitter()
      .removeListener(FormPropertyShareStatechartActionEnum.cacheModel, this.onCacheModelActionStatechart);

    this.propertyShareStatechart
      .getEventEmitter()
      .removeListener(
        FormPropertyShareStatechartActionEnum.setSubmittingStatus,
        this.onSetSubmittingStatusActionStatechart
      );

    this.propertyShareStatechart
      .getEventEmitter()
      .removeListener(
        FormPropertyShareStatechartActionEnum.resetSubmittingStatus,
        this.onResetSubmittingStatusActionStatechart
      );

    this.propertyShareStatechart
      .getEventEmitter()
      .removeListener(FormPropertyShareStatechartActionEnum.executeCaptcha, this.onExecuteCaptchaActionStatechart);

    this.propertyShareStatechart
      .getEventEmitter()
      .removeListener(FormPropertyShareStatechartActionEnum.validateInput, this.onValidateInputActionStatechart);

    this.propertyShareStatechart
      .getEventEmitter()
      .removeListener(
        FormPropertyShareStatechartActionEnum.sendSubmitRequest,
        this.onSendSubmitRequestActionStatechart
      );

    this.propertyShareStatechart
      .getEventEmitter()
      .removeListener(
        FormPropertyShareStatechartActionEnum.setSubmittedStatus,
        this.onSetSubmittedStatusActionStatechart
      );

    this.propertyShareStatechart
      .getEventEmitter()
      .removeListener(FormPropertyShareStatechartActionEnum.showErrors, this.onShowErrorsActionStatechart);
  }

  /**
   * @inheritDoc
   */
  public getState(): FormPropertyShareTemplatePropsInterface {
    return this.state;
  }

  /**
   * Set a model for the store
   */
  public updateModel(model: JsonApiModelInterface): void {
    this.setState({
      ...this.getState(),
      model,
    });
  }

  /**
   * Update input data
   *
   * @param name  - Input name
   * @param value - New value
   */
  public updateInput(name: keyof FormPropertyShareInputInterface, value: string): void {
    const state: FormPropertyShareTemplatePropsInterface = {
      ...this.state,
    };

    // Get state input
    const input = state.input[name];

    // Update input
    input.value = value;
    input.error = this.validateInput(input, this.getValidators()[name]);

    this.setState(state, this.updateInput);
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.captcha.getEventEmitter().addListener(GoogleRecaptchaViewStoreEvent.change, this.onChangeCaptchaViewStore);

    this.propertyShareStatechart
      .getEventEmitter()
      .addListener(FormPropertyShareStatechartActionEnum.cacheModel, this.onCacheModelActionStatechart);

    this.propertyShareStatechart
      .getEventEmitter()
      .addListener(
        FormPropertyShareStatechartActionEnum.setSubmittingStatus,
        this.onSetSubmittingStatusActionStatechart
      );

    this.propertyShareStatechart
      .getEventEmitter()
      .addListener(
        FormPropertyShareStatechartActionEnum.resetSubmittingStatus,
        this.onResetSubmittingStatusActionStatechart
      );

    this.propertyShareStatechart
      .getEventEmitter()
      .addListener(FormPropertyShareStatechartActionEnum.executeCaptcha, this.onExecuteCaptchaActionStatechart);

    this.propertyShareStatechart
      .getEventEmitter()
      .addListener(FormPropertyShareStatechartActionEnum.validateInput, this.onValidateInputActionStatechart);

    this.propertyShareStatechart
      .getEventEmitter()
      .addListener(FormPropertyShareStatechartActionEnum.sendSubmitRequest, this.onSendSubmitRequestActionStatechart);

    this.propertyShareStatechart
      .getEventEmitter()
      .addListener(FormPropertyShareStatechartActionEnum.setSubmittedStatus, this.onSetSubmittedStatusActionStatechart);

    this.propertyShareStatechart
      .getEventEmitter()
      .addListener(FormPropertyShareStatechartActionEnum.showErrors, this.onShowErrorsActionStatechart);
  }

  /**
   * Return the input validators for all fields
   */
  private getValidators(): {
    [key in keyof FormPropertyShareInputInterface]: InputValidatorInterface[];
  } {
    return {
      email: [
        new InputValidatorRequired(i18nTranslate('Please enter your email')),
        new InputValidatorEmail(i18nTranslate('Please enter a valid email')),
      ],
      friendEmail: [
        new InputValidatorRequired(i18nTranslate("Enter friend's email")),
        new InputValidatorEmail(i18nTranslate('Please enter a valid email')),
      ],
      message: [new InputValidatorRequired(i18nTranslate("Value is required and can't be empty"))],
      captcha: [],
    };
  }

  /**
   * Validate the input and returns error message if any
   */
  private validateInput(input: InputStateInterface, validators: InputValidatorInterface[]): string {
    // Input has validators
    if (validators && validators.length) {
      const errors: string[] = validators
        .map((validator: InputValidatorInterface) => validator.getError(input.value, this.state.input))
        .filter((message: string) => !!message);
      // Only keep first error if any
      return errors[0] || '';
    }

    return '';
  }

  /**
   * Changed input
   */
  private onChange = (e: Event, fieldName: keyof FormPropertyShareInputInterface): void => {
    this.updateInput(fieldName, (<HTMLInputElement>e.target).value);
  };

  /**
   * Blur form element
   */
  private onBlur = (e: Event, fieldName: keyof FormPropertyShareInputInterface) => {
    const state = this.getState();

    if (state.status.submitted) {
      return;
    }

    this.updateInput(fieldName, state.input[fieldName].value);
  };

  /**
   * Clicked submit button
   */
  private onSubmit = (e: MouseEvent): void => {
    e.preventDefault();

    this.propertyShareStatechart.transit({
      event: FormPropertyShareStatechartEvent.wantToSubmitForm,
      payload: null,
    });
  };

  /**
   * Clicked close button
   */
  private onClickClose = (): void => {
    this.eventEmitter.emit(FormPropertyShareViewStoreEvent.close);
  };

  /**
   * Captcha value has changed
   */
  private onChangeCaptchaViewStore = () => {
    const state = this.getState();

    state.input.captcha.value = this.captcha.getState().value;

    this.setState(state);

    this.propertyShareStatechart.transit({
      event: FormPropertyShareStatechartEvent.captchaValueChanged,
      payload: null,
    });
  };

  /**
   * Cache property share model for form fields pre population
   */
  private onCacheModelActionStatechart = (model: PropertyShareModel) => {
    this.browserStorageLocalService.setData(this.cachedModelKey, model);
  };

  /**
   * Set submitting status
   */
  private onSetSubmittingStatusActionStatechart = () => {
    this.state.status.submitting = true;
    this.state.error = '';
    this.setState(this.state);
  };

  /**
   * Set submitting status
   */
  private onResetSubmittingStatusActionStatechart = () => {
    this.state.status.submitting = false;
    this.setState(this.state);
  };

  /**
   * Set submitted status
   */
  private onSetSubmittedStatusActionStatechart = () => {
    const state = this.getState();

    state.status.submitting = false;
    state.status.submitted = true;

    this.setState(state);
  };

  /**
   * Execute captcha
   */
  private onExecuteCaptchaActionStatechart = () => {
    if (!this.state.input.captcha.value) {
      this.captcha.execute();

      return;
    }

    this.propertyShareStatechart.transit({
      event: FormPropertyShareStatechartEvent.captchaValueChanged,
      payload: null,
    });
  };

  /**
   * Validate user input
   */
  private onValidateInputActionStatechart = () => {
    const state = this.getState();

    // Validate all inputs
    const validators = this.getValidators();

    // Map any errors
    const errors = Object.keys(this.state.input)
      .map((name: keyof FormPropertyShareInputInterface) => {
        // Get state input
        const input = this.state.input[name];

        // Update input
        input.error = this.validateInput(input, validators[name]);

        return input;
      })
      .filter((input: InputStateInterface) => input.error);

    // Update form state
    this.setState(state, this.onSubmit);

    // Form is not valid
    if (errors.length) {
      this.propertyShareStatechart.transit({
        event: FormPropertyShareStatechartEvent.inputIsInvalid,
        payload: null,
      });

      return;
    }

    this.propertyShareStatechart.transit({
      event: FormPropertyShareStatechartEvent.inputIsValid,
      payload: null,
    });
  };

  /**
   * Send submit request
   */
  private onSendSubmitRequestActionStatechart = () => {
    // Current state
    const state = this.getState();

    const model = new FormPropertyShareModelAdapter().toPropertyShareModel(state.input);

    this.propertyShareStore
      .create(state.model.id, model)
      .then(() => {
        this.propertyShareStatechart.transit({
          event: FormPropertyShareStatechartEvent.submitSuccessful,
          payload: model,
        });

        this.getEventEmitter().emit(FormPropertyShareViewStoreEvent.submit, state.input);
      })
      .catch((response) => {
        this.propertyShareStatechart.transit({
          event: FormPropertyShareStatechartEvent.submitFailed,
          payload: response,
        });
      });
  };

  /**
   * Show backend errors
   */
  private onShowErrorsActionStatechart = (response: Error | HttpApiResponseInterface) => {
    if (response instanceof Error) {
      throw new Error(response.message);
    }

    const state = this.getState();

    if (state.captchaRequired) {
      // Captcha tokens can only be used once, so reset it
      this.captcha.reset();
    }

    if (response.data.errors && response.data.errors.length) {
      // Remap the inputs based on the API response
      state.input = new FormPropertyShareInputAdapter(response, state.input).getData();
    } else {
      state.error = i18nTranslate(
        'Sorry, an error occurred while processing your request, please try again in few minutes.'
      );
    }

    this.setState(state);
  };
}
